import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { useLanguage } from '../../context/LanguageContext';

const WorkOrderList = () => {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const isAr = lang === 'ar';
  const token = localStorage.getItem('token');

  // State
  const [workOrders, setWorkOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 0 });
  const [agents, setAgents] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    search: '', status: '', priority: '', assigned_to: '',
    wo_type: '', source: '', scheduled_from: '', scheduled_to: '',
    sort_by: 'created_at', sort_order: 'DESC'
  });

  const handleFilter = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1);
  };

  // Labels
  const L = {
    title: isAr ? 'أوامر العمل' : 'Work Orders',
    new_wo: isAr ? 'أمر عمل جديد' : 'New Work Order',
    search: isAr ? 'بحث...' : 'Search...',
    filters: isAr ? 'فلاتر' : 'Filters',
    status: isAr ? 'الحالة' : 'Status',
    priority: isAr ? 'الأولوية' : 'Priority',
    type: isAr ? 'النوع' : 'Type',
    source: isAr ? 'المصدر' : 'Source',
    assigned: isAr ? 'مسند إلى' : 'Assigned To',
    from_date: isAr ? 'من تاريخ' : 'From Date',
    to_date: isAr ? 'إلى تاريخ' : 'To Date',
    clear: isAr ? 'مسح' : 'Clear',
    wo_number: isAr ? 'رقم الأمر' : 'WO #',
    equipment: isAr ? 'المعدات' : 'Equipment',
    client: isAr ? 'العميل' : 'Client',
    scheduled: isAr ? 'الموعد' : 'Scheduled',
    created: isAr ? 'تاريخ الإنشاء' : 'Created',
    sla: isAr ? 'SLA' : 'SLA',
    no_data: isAr ? 'لا توجد أوامر عمل' : 'No work orders found',
    open: isAr ? 'مفتوح' : 'Open',
    overdue: isAr ? 'متأخر' : 'Overdue',
    completed_month: isAr ? 'مكتمل هذا الشهر' : 'Completed This Month',
    avg_resolution: isAr ? 'متوسط الحل' : 'Avg Resolution',
    sla_compliance: isAr ? 'التزام SLA' : 'SLA Compliance',
    total: isAr ? 'الإجمالي' : 'Total',
    all: isAr ? 'الكل' : 'All',
    hours: isAr ? 'ساعة' : 'hrs',
    prev: isAr ? 'السابق' : 'Previous',
    next: isAr ? 'التالي' : 'Next',
    loading: isAr ? 'جاري التحميل...' : 'Loading...',
  };

  const STATUS_OPTIONS = [
    { value: 'draft', label: isAr ? 'مسودة' : 'Draft', color: '#6b7280' },
    { value: 'open', label: isAr ? 'مفتوح' : 'Open', color: '#3b82f6' },
    { value: 'assigned', label: isAr ? 'مسند' : 'Assigned', color: '#8b5cf6' },
    { value: 'in_progress', label: isAr ? 'قيد التنفيذ' : 'In Progress', color: '#f59e0b' },
    { value: 'on_hold', label: isAr ? 'معلق' : 'On Hold', color: '#ef4444' },
    { value: 'completed', label: isAr ? 'مكتمل' : 'Completed', color: '#10b981' },
    { value: 'closed', label: isAr ? 'مغلق' : 'Closed', color: '#6b7280' },
    { value: 'cancelled', label: isAr ? 'ملغي' : 'Cancelled', color: '#991b1b' },
  ];

  const PRIORITY_OPTIONS = [
    { value: 'critical', label: isAr ? 'حرج' : 'Critical', color: '#dc2626' },
    { value: 'high', label: isAr ? 'عالي' : 'High', color: '#f97316' },
    { value: 'medium', label: isAr ? 'متوسط' : 'Medium', color: '#eab308' },
    { value: 'low', label: isAr ? 'منخفض' : 'Low', color: '#22c55e' },
  ];

  const TYPE_OPTIONS = [
    { value: 'corrective', label: isAr ? 'تصحيحي' : 'Corrective' },
    { value: 'preventive', label: isAr ? 'وقائي' : 'Preventive' },
    { value: 'inspection', label: isAr ? 'فحص' : 'Inspection' },
    { value: 'emergency', label: isAr ? 'طوارئ' : 'Emergency' },
    { value: 'installation', label: isAr ? 'تركيب' : 'Installation' },
  ];

  const SOURCE_OPTIONS = [
    { value: 'manual', label: isAr ? 'يدوي' : 'Manual' },
    { value: 'maintenance_plan', label: isAr ? 'خطة صيانة' : 'Maintenance Plan' },
    { value: 'helpdesk', label: isAr ? 'الدعم الفني' : 'Helpdesk' },
    { value: 'whatsapp_bot', label: isAr ? 'بوت واتساب' : 'WhatsApp Bot' },
    { value: 'qr_scan', label: isAr ? 'مسح QR' : 'QR Scan' },
  ];

  // Fetch data
  const fetchWorkOrders = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit: 20 });
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });

      const res = await fetch(`/api/v1/fsm/work-orders?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setWorkOrders(data.data);
        setPagination(data.pagination);
      }
    } catch (err) {
      console.error('Fetch WO error:', err);
    } finally {
      setLoading(false);
    }
  }, [page, filters, token]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch('/api/v1/fsm/work-orders/stats', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setStats(data.data);
    } catch (err) {
      console.error('Fetch stats error:', err);
    }
  }, [token]);

  const fetchAgents = useCallback(async () => {
    try {
      const res = await fetch('/api/v1/agents', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setAgents(data.data || data.agents || []);
    } catch (err) {
      console.error('Fetch agents error:', err);
    }
  }, [token]);

  useEffect(() => { fetchWorkOrders(); }, [fetchWorkOrders]);
  useEffect(() => { fetchStats(); fetchAgents(); }, [fetchStats, fetchAgents]);

  // Helpers
  const getStatusInfo = (status) => STATUS_OPTIONS.find(s => s.value === status) || { label: status, color: '#6b7280' };
  const getPriorityInfo = (priority) => PRIORITY_OPTIONS.find(p => p.value === priority) || { label: priority, color: '#6b7280' };
  const formatDate = (d) => d ? new Date(d).toLocaleDateString(isAr ? 'ar-EG' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';

  const getSLAIndicator = (wo) => {
    if (!wo.sla_resolution_deadline) return null;
    if (wo.sla_resolution_met === 1) return { icon: '✅', color: '#10b981', label: isAr ? 'ملتزم' : 'Met' };
    if (wo.sla_resolution_met === 0) return { icon: '❌', color: '#ef4444', label: isAr ? 'مخالف' : 'Breached' };
    const deadline = new Date(wo.sla_resolution_deadline);
    const now = new Date();
    if (deadline < now) return { icon: '🔴', color: '#ef4444', label: isAr ? 'متأخر' : 'Overdue' };
    const hoursLeft = (deadline - now) / (1000 * 60 * 60);
    if (hoursLeft < 4) return { icon: '🟡', color: '#f59e0b', label: `${Math.round(hoursLeft)}h` };
    return { icon: '🟢', color: '#10b981', label: `${Math.round(hoursLeft)}h` };
  };

  const clearFilters = () => {
    setFilters({ search: '', status: '', priority: '', assigned_to: '', wo_type: '', source: '', scheduled_from: '', scheduled_to: '', sort_by: 'created_at', sort_order: 'DESC' });
    setPage(1);
  };

  // Styles
  const containerSt = { padding: '24px', minHeight: '100vh', direction: isAr ? 'rtl' : 'ltr' };
  const cardSt = { backgroundColor: 'rgba(26, 32, 56, 0.8)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)', padding: '20px' };
  const statCardSt = (color) => ({
    ...cardSt, padding: '16px', flex: '1', minWidth: '160px',
    borderLeft: isAr ? 'none' : `3px solid ${color}`,
    borderRight: isAr ? `3px solid ${color}` : 'none',
  });
  const inputSt = {
    width: '100%', padding: '10px 12px', border: '2px solid rgba(255,255,255,0.15)',
    borderRadius: '8px', backgroundColor: '#1a2038', color: '#f9fafb',
    fontSize: '13px', outline: 'none', boxSizing: 'border-box',
  };
  const selectSt = { ...inputSt, cursor: 'pointer' };
  const labelSt = { display: 'block', marginBottom: '4px', color: '#9ca3af', fontSize: '11px', fontWeight: '500' };
  const btnPrimary = {
    padding: '10px 20px', backgroundColor: '#667eea', color: '#fff', border: 'none',
    borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600',
    display: 'flex', alignItems: 'center', gap: '8px',
  };
  const btnOutline = {
    padding: '8px 16px', backgroundColor: 'transparent', color: '#9ca3af',
    border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', cursor: 'pointer', fontSize: '13px',
  };
  const badgeSt = (color) => ({
    display: 'inline-block', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600',
    backgroundColor: `${color}22`, color: color, whiteSpace: 'nowrap',
  });
  const thSt = {
    padding: '12px 14px', textAlign: isAr ? 'right' : 'left', fontSize: '11px', fontWeight: '600',
    color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
  };
  const tdSt = {
    padding: '12px 14px', fontSize: '13px', color: '#d1d5db',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  };
  const rowHoverSt = { cursor: 'pointer', transition: 'background-color 0.15s' };

  return (
    <Layout>
    <div style={containerSt}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ color: '#f9fafb', fontSize: '24px', fontWeight: '700', margin: 0 }}>🔧 {L.title}</h1>
          <p style={{ color: '#9ca3af', fontSize: '13px', margin: '4px 0 0' }}>
            {stats ? `${stats.total} ${L.total}` : ''}
          </p>
        </div>
        <button style={btnPrimary} onClick={() => navigate('/fsm/work-orders/new')}>
          <span>+</span> {L.new_wo}
        </button>
      </div>

      {/* Stat Cards */}
      {stats && (
        <div style={{ display: 'flex', gap: '14px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <div style={statCardSt('#3b82f6')}>
            <div style={{ color: '#9ca3af', fontSize: '12px', marginBottom: '4px' }}>{L.open}</div>
            <div style={{ color: '#f9fafb', fontSize: '28px', fontWeight: '700' }}>{stats.open}</div>
          </div>
          <div style={statCardSt('#ef4444')}>
            <div style={{ color: '#9ca3af', fontSize: '12px', marginBottom: '4px' }}>{L.overdue}</div>
            <div style={{ color: '#ef4444', fontSize: '28px', fontWeight: '700' }}>{stats.overdue}</div>
          </div>
          <div style={statCardSt('#10b981')}>
            <div style={{ color: '#9ca3af', fontSize: '12px', marginBottom: '4px' }}>{L.completed_month}</div>
            <div style={{ color: '#f9fafb', fontSize: '28px', fontWeight: '700' }}>{stats.completed_this_month}</div>
          </div>
          <div style={statCardSt('#f59e0b')}>
            <div style={{ color: '#9ca3af', fontSize: '12px', marginBottom: '4px' }}>{L.avg_resolution}</div>
            <div style={{ color: '#f9fafb', fontSize: '28px', fontWeight: '700' }}>
              {stats.avg_resolution_hours != null ? `${stats.avg_resolution_hours}${L.hours}` : '-'}
            </div>
          </div>
          <div style={statCardSt('#8b5cf6')}>
            <div style={{ color: '#9ca3af', fontSize: '12px', marginBottom: '4px' }}>{L.sla_compliance}</div>
            <div style={{ color: '#f9fafb', fontSize: '28px', fontWeight: '700' }}>
              {stats.sla_resolution_compliance != null ? `${stats.sla_resolution_compliance}%` : '-'}
            </div>
          </div>
        </div>
      )}

      {/* Search + Filter Toggle */}
      <div style={{ ...cardSt, marginBottom: '16px', padding: '16px' }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <input
              type="text" placeholder={L.search} value={filters.search}
              onChange={(e) => handleFilter('search', e.target.value)}
              style={{ ...inputSt, padding: '10px 14px' }}
            />
          </div>
          <button style={btnOutline} onClick={() => setShowFilters(!showFilters)}>
            🔽 {L.filters} {showFilters ? '▲' : '▼'}
          </button>
          {Object.values(filters).some(v => v && v !== 'created_at' && v !== 'DESC') && (
            <button style={{ ...btnOutline, color: '#ef4444', borderColor: '#ef4444' }} onClick={clearFilters}>
              ✕ {L.clear}
            </button>
          )}
        </div>

        {/* Expanded Filters */}
        {showFilters && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <div>
              <label style={labelSt}>{L.status}</label>
              <select style={selectSt} value={filters.status} onChange={(e) => handleFilter('status', e.target.value)}>
                <option value="">{L.all}</option>
                {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label style={labelSt}>{L.priority}</label>
              <select style={selectSt} value={filters.priority} onChange={(e) => handleFilter('priority', e.target.value)}>
                <option value="">{L.all}</option>
                {PRIORITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label style={labelSt}>{L.type}</label>
              <select style={selectSt} value={filters.wo_type} onChange={(e) => handleFilter('wo_type', e.target.value)}>
                <option value="">{L.all}</option>
                {TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label style={labelSt}>{L.source}</label>
              <select style={selectSt} value={filters.source} onChange={(e) => handleFilter('source', e.target.value)}>
                <option value="">{L.all}</option>
                {SOURCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label style={labelSt}>{L.assigned}</label>
              <select style={selectSt} value={filters.assigned_to} onChange={(e) => handleFilter('assigned_to', e.target.value)}>
                <option value="">{L.all}</option>
                {agents.map(a => <option key={a.id} value={a.id}>{a.full_name}</option>)}
              </select>
            </div>
            <div>
              <label style={labelSt}>{L.from_date}</label>
              <input type="date" style={inputSt} value={filters.scheduled_from} onChange={(e) => handleFilter('scheduled_from', e.target.value)} />
            </div>
            <div>
              <label style={labelSt}>{L.to_date}</label>
              <input type="date" style={inputSt} value={filters.scheduled_to} onChange={(e) => handleFilter('scheduled_to', e.target.value)} />
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div style={{ ...cardSt, padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center', color: '#9ca3af' }}>{L.loading}</div>
        ) : workOrders.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>📋</div>
            <div style={{ color: '#9ca3af', fontSize: '15px' }}>{L.no_data}</div>
            <button style={{ ...btnPrimary, margin: '16px auto', display: 'inline-flex' }} onClick={() => navigate('/fsm/work-orders/new')}>
              + {L.new_wo}
            </button>
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}>
                    <th style={thSt}>{L.wo_number}</th>
                    <th style={thSt}>{L.title}</th>
                    <th style={thSt}>{L.equipment}</th>
                    <th style={thSt}>{L.client}</th>
                    <th style={thSt}>{L.priority}</th>
                    <th style={thSt}>{L.status}</th>
                    <th style={thSt}>{L.assigned}</th>
                    <th style={thSt}>{L.sla}</th>
                    <th style={thSt}>{L.scheduled}</th>
                    <th style={thSt}>{L.created}</th>
                  </tr>
                </thead>
                <tbody>
                  {workOrders.map((wo) => {
                    const statusInfo = getStatusInfo(wo.status);
                    const priorityInfo = getPriorityInfo(wo.priority);
                    const slaInd = getSLAIndicator(wo);
                    return (
                      <tr
                        key={wo.id}
                        style={rowHoverSt}
                        onClick={() => navigate(`/fsm/work-orders/${wo.id}`)}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.04)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <td style={{ ...tdSt, color: '#667eea', fontWeight: '600', whiteSpace: 'nowrap' }}>{wo.wo_number}</td>
                        <td style={{ ...tdSt, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {isAr && wo.title_ar ? wo.title_ar : wo.title}
                        </td>
                        <td style={{ ...tdSt, whiteSpace: 'nowrap' }}>
                          {isAr && wo.equipment_name_ar ? wo.equipment_name_ar : wo.equipment_name || '-'}
                        </td>
                        <td style={{ ...tdSt, whiteSpace: 'nowrap' }}>
                          {isAr && wo.client_name_ar ? wo.client_name_ar : wo.client_name || '-'}
                        </td>
                        <td style={tdSt}>
                          <span style={badgeSt(priorityInfo.color)}>{priorityInfo.label}</span>
                        </td>
                        <td style={tdSt}>
                          <span style={badgeSt(statusInfo.color)}>{statusInfo.label}</span>
                        </td>
                        <td style={{ ...tdSt, whiteSpace: 'nowrap' }}>{wo.assigned_name || '-'}</td>
                        <td style={tdSt}>
                          {slaInd ? (
                            <span style={{ color: slaInd.color, fontSize: '12px', fontWeight: '600' }}>
                              {slaInd.icon} {slaInd.label}
                            </span>
                          ) : '-'}
                        </td>
                        <td style={{ ...tdSt, whiteSpace: 'nowrap' }}>{formatDate(wo.scheduled_date)}</td>
                        <td style={{ ...tdSt, whiteSpace: 'nowrap', color: '#9ca3af', fontSize: '12px' }}>{formatDate(wo.created_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '14px 20px', borderTop: '1px solid rgba(255,255,255,0.1)',
              }}>
                <span style={{ color: '#9ca3af', fontSize: '13px' }}>
                  {isAr ? `صفحة ${page} من ${pagination.pages} (${pagination.total} نتيجة)` : `Page ${page} of ${pagination.pages} (${pagination.total} results)`}
                </span>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    style={{ ...btnOutline, opacity: page <= 1 ? 0.4 : 1 }}
                    disabled={page <= 1}
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                  >
                    {L.prev}
                  </button>
                  <button
                    style={{ ...btnOutline, opacity: page >= pagination.pages ? 0.4 : 1 }}
                    disabled={page >= pagination.pages}
                    onClick={() => setPage(p => p + 1)}
                  >
                    {L.next}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
    </Layout>
  );
};

export default WorkOrderList;
